import { CANDIDATE_TAGS, COMPETENCY_LEVEL } from 'domain/shared/newVacancy';
import isEqual from 'lodash/isEqual';
import type {
  SavedSourcingFilterParameters,
  SourcingCompetencyLevel,
  SourcingFilterKey,
  SourcingFilterOptions,
  SourcingFilters,
  SourcingSavedFilter,
} from '../../../types';
import {
  SOURCING_FILTER_KEYS,
  SOURCING_FILTER_LABELS,
  SOURCING_FILTERS,
  SUPPORTED_COMPETENCY_LEVELS,
} from '../constants';
import { getComparableValues, hasFilterValue } from './sourcingFilterNormalization';
import { mapSourcingFiltersToParameters, mapSourcingParametersToFilters } from './sourcingFilterModel';
import { MAP_ENGLISH_LEVEL_TO_SHORT } from 'domain/shared/candidate/model';

export const cloneFilters = (filters: SourcingFilters): SourcingFilters => ({
  ...filters,
  ...(filters.stricts ? { stricts: [...filters.stricts] } : {}),
  ...(filters.levels ? { levels: [...filters.levels] } : {}),
  ...(filters.skills ? { skills: [...filters.skills] } : {}),
  ...(filters.location ? { location: [...filters.location] } : {}),
  ...(filters.tags ? { tags: [...filters.tags] } : {}),
  ...(filters.lastInteraction ? { lastInteraction: { ...filters.lastInteraction } } : {}),
});

export const formatSourcingLevels = (levels: SourcingCompetencyLevel[]): string => {
  const selected = Array.from(new Set(levels))
    .filter((level): level is (typeof SUPPORTED_COMPETENCY_LEVELS)[number] =>
      (SUPPORTED_COMPETENCY_LEVELS as readonly string[]).includes(level),
    )
    .sort((left, right) => SUPPORTED_COMPETENCY_LEVELS.indexOf(left) - SUPPORTED_COMPETENCY_LEVELS.indexOf(right));

  const numericLevels = selected.filter(level => level !== 'N_A');
  const ranges: string[] = [];
  let rangeStart: number | undefined;
  let rangeEnd: number | undefined;

  const flushRange = () => {
    if (rangeStart === undefined || rangeEnd === undefined) return;
    const first = `L${rangeStart}`;
    const last = `L${rangeEnd}`;
    ranges.push(rangeStart === rangeEnd ? first : `${first}-${last}`);
  };

  numericLevels.forEach(level => {
    const current = Number(level.slice(1));
    if (rangeEnd !== undefined && current !== rangeEnd + 1) {
      flushRange();
      rangeStart = current;
    } else if (rangeStart === undefined) {
      rangeStart = current;
    }
    rangeEnd = current;
  });
  flushRange();

  if (selected.includes('N_A')) ranges.push(COMPETENCY_LEVEL.N_A);
  return ranges.join(',');
};

export const getSourcingFilterValue = (
  key: SourcingFilterKey,
  filters: SourcingFilters,
  options: SourcingFilterOptions,
): string | null => {
  if (key === SOURCING_FILTERS.specialization) {
    return (
      options.specializations.find(item => item.specializationId === filters.specializationId)?.specialization ?? null
    );
  }
  if (key === SOURCING_FILTERS.technology) {
    return (
      options.specializations
        .flatMap(item => item.technologies)
        .find(item => item.technologyId === filters.technologyId)?.technology ?? null
    );
  }
  if (key === SOURCING_FILTERS.levels) {
    return filters.levels ? formatSourcingLevels(filters.levels) : null;
  }
  if (key === SOURCING_FILTERS.lastInteraction) {
    const value = filters.lastInteraction;
    return [value?.from && `From ${value.from}`, value?.to && `Until ${value.to}`].filter(Boolean).join(' ');
  }

  if (key === SOURCING_FILTERS.tags) {
    return filters.tags?.length ? filters.tags.map(item => CANDIDATE_TAGS[item]).join(', ') : null;
  }

  const value = filters[key];
  return hasFilterValue(value) ? (Array.isArray(value) ? value.join(', ') : String(value)) : null;
};

export const getFilterSummary = (
  parameters: SavedSourcingFilterParameters,
  specializationName: string,
  technologyName?: string,
): string => {
  const filters = mapSourcingParametersToFilters(parameters);
  const values: Partial<Record<SourcingFilterKey, string>> = {};

  if (filters.specializationId !== undefined) {
    values[SOURCING_FILTERS.specialization] = specializationName || String(filters.specializationId);
  }
  if (filters.technologyId) {
    values[SOURCING_FILTERS.technology] = technologyName || filters.technologyId;
  }
  if (filters.levels?.length) {
    values[SOURCING_FILTERS.levels] = formatSourcingLevels(filters.levels);
  }
  if (filters.skills?.length) {
    values[SOURCING_FILTERS.skills] = filters.skills.join(', ');
  }
  if (filters.englishLevel) {
    values[SOURCING_FILTERS.englishLevel] = MAP_ENGLISH_LEVEL_TO_SHORT[filters.englishLevel] ?? filters.englishLevel;
  }
  if (filters.location?.length) {
    values[SOURCING_FILTERS.location] = filters.location.join(', ');
  }
  if (filters.tags?.length) {
    values[SOURCING_FILTERS.tags] = filters.tags.join(', ');
  }
  if (filters.lastInteraction?.from || filters.lastInteraction?.to) {
    values[SOURCING_FILTERS.lastInteraction] = [
      filters.lastInteraction.from && `From ${filters.lastInteraction.from}`,
      filters.lastInteraction.to && `Until ${filters.lastInteraction.to}`,
    ]
      .filter(Boolean)
      .join(' ');
  }

  return SOURCING_FILTER_KEYS.flatMap(key => {
    const value = values[key];
    return value ? [`${SOURCING_FILTER_LABELS[key]}: ${value}`] : [];
  }).join('; ');
};

const getComparableSourcingParameters = (filters: SourcingFilters) => {
  const parameters = mapSourcingFiltersToParameters(filters);

  return {
    ...parameters,
    levels: parameters.levels
      ? { ...parameters.levels, value: getComparableValues(parameters.levels.value) }
      : undefined,
    skills: parameters.skills
      ? { ...parameters.skills, value: getComparableValues(parameters.skills.value) }
      : undefined,
    location: parameters.location
      ? { ...parameters.location, value: getComparableValues(parameters.location.value) }
      : undefined,
    tags: parameters.tags ? { ...parameters.tags, value: getComparableValues(parameters.tags.value) } : undefined,
  };
};

export const findMatchingSavedSourcingFilter = (
  filters: SourcingFilters,
  savedFilters: SourcingSavedFilter[],
): SourcingSavedFilter | undefined => {
  const currentParameters = getComparableSourcingParameters(filters);

  return savedFilters.find(savedFilter =>
    isEqual(currentParameters, getComparableSourcingParameters(mapSourcingParametersToFilters(savedFilter.parameters))),
  );
};
